(function (global) {
  'use strict';

  var SELECTOR = 'article.efa-slide-has-cta';
  var SAFE_MARGIN = 10;
  var VERSION = '1.3.0';

  function clampPosition(value) {
    var number = parseFloat(value);
    if (!Number.isFinite(number)) return 0;
    return Math.max(-100, Math.min(100, number));
  }

  /**
   * Converts the -100..100 editor control into the useful travel available
   * around the position established by "Content position". -100 reaches the
   * safe start edge, 0 keeps the safe base anchor and +100 reaches the safe end
   * edge. The complete title + CTA group therefore remains 10 px inside.
   */
  function calculateAxis(position, frameStart, frameEnd, groupStart, groupEnd, margin) {
    var progress = clampPosition(position) / 100;
    var safeMargin = Number.isFinite(margin) ? Math.max(0, margin) : SAFE_MARGIN;
    var minimum = frameStart + safeMargin - groupStart;
    var maximum = frameEnd - safeMargin - groupEnd;
    var shift;

    if (minimum <= maximum) {
      var safeBase = Math.max(minimum, Math.min(maximum, 0));
      if (progress < 0) {
        shift = safeBase + (-progress * (minimum - safeBase));
      } else {
        shift = safeBase + (progress * (maximum - safeBase));
      }
    } else {
      // The group is larger than the safe frame. Centring is the least lossy
      // deterministic fallback; the overflow marker still exposes this case.
      shift = ((frameStart + frameEnd) - (groupStart + groupEnd)) / 2;
    }

    if (!Number.isFinite(shift)) return 0;
    shift = Math.round(shift * 1000) / 1000;
    return Object.is(shift, -0) ? 0 : shift;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { calculateAxis: calculateAxis, clampPosition: clampPosition };
  }

  if (!global || !global.document) return;

  var document = global.document;
  var existing = global.EFASlideshowPosition;
  if (existing && existing.version === VERSION && typeof existing.refresh === 'function') {
    existing.refresh(document);
    return;
  }
  if (existing && typeof existing.destroy === 'function') existing.destroy();

  var records = new Map();
  var nodeOwners = new WeakMap();
  var pending = new Set();
  var frameId = 0;
  var destroyed = false;

  function directChildByClass(parent, className) {
    for (var index = 0; index < parent.children.length; index += 1) {
      var child = parent.children[index];
      if (child.classList && child.classList.contains(className)) return child;
    }
    return null;
  }

  function directTitle(article) {
    return directChildByClass(article, 'title-styling');
  }

  function positionTarget(article, title) {
    if (!article.classList.contains('w-aside')) return title;
    return directChildByClass(title, 'efa-slide-copy-anchor');
  }

  function visibleRect(node) {
    var rect = node.getBoundingClientRect();
    if (!rect || !Number.isFinite(rect.left) || !Number.isFinite(rect.top)) return null;
    if (rect.width <= 0 || rect.height <= 0) return null;
    return rect;
  }

  function unionOf(nodes) {
    var union = null;

    nodes.forEach(function (node) {
      var rect = visibleRect(node);
      if (!rect) return;

      if (!union) {
        union = { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
        return;
      }

      union.left = Math.min(union.left, rect.left);
      union.top = Math.min(union.top, rect.top);
      union.right = Math.max(union.right, rect.right);
      union.bottom = Math.max(union.bottom, rect.bottom);
    });

    return union;
  }

  function cssNumber(element, property) {
    var value = global.getComputedStyle(element).getPropertyValue(property);
    var number = parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  function px(value) {
    return String(value) + 'px';
  }

  function setShift(record, x, y) {
    if (record.shiftX !== x) {
      record.title.style.setProperty('--efa-slide-shift-x', px(x));
      record.shiftX = x;
    }
    if (record.shiftY !== y) {
      record.title.style.setProperty('--efa-slide-shift-y', px(y));
      record.shiftY = y;
    }
  }

  function measure(article) {
    var record = records.get(article);
    if (!record || !article.isConnected || !record.title.isConnected) return null;

    var frame = visibleRect(record.frame);
    var group = unionOf(record.nodes);
    if (!frame || !group) return null;

    // getBoundingClientRect includes the shift written during the previous pass.
    // Removing that known delta recovers the immutable, centred zero geometry
    // without temporarily changing styles or causing a second layout pass.
    var currentX = record.shiftX;
    var currentY = record.shiftY;
    var zeroGroup = {
      left: group.left - currentX,
      right: group.right - currentX,
      top: group.top - currentY,
      bottom: group.bottom - currentY
    };
    var style = global.getComputedStyle(article);
    var positionX = style.getPropertyValue('--efa-slide-pos-x');
    var positionY = style.getPropertyValue('--efa-slide-pos-y');
    var shiftX = calculateAxis(positionX, frame.left, frame.right, zeroGroup.left, zeroGroup.right, SAFE_MARGIN);
    var shiftY = calculateAxis(positionY, frame.top, frame.bottom, zeroGroup.top, zeroGroup.bottom, SAFE_MARGIN);

    var overflow = [];
    if (zeroGroup.right - zeroGroup.left > frame.width - SAFE_MARGIN * 2) overflow.push('x');
    if (zeroGroup.bottom - zeroGroup.top > frame.height - SAFE_MARGIN * 2) overflow.push('y');
    return { article: article, record: record, shiftX: shiftX, shiftY: shiftY, overflow: overflow };
  }

  function applyMeasurement(result) {
    if (!result) return;
    setShift(result.record, result.shiftX, result.shiftY);
    result.article.setAttribute('data-efa-position-ready', '');
    if (result.overflow.length) {
      result.article.setAttribute('data-efa-position-overflow', result.overflow.join(' '));
    } else {
      result.article.removeAttribute('data-efa-position-overflow');
    }
  }

  function flush() {
    frameId = 0;
    var batch = Array.from(pending);
    pending.clear();
    // Read every rectangle first, then write every result. This prevents one
    // slide's style mutation from forcing a new synchronous layout for the next.
    batch.map(measure).forEach(applyMeasurement);
  }

  function schedule(article) {
    if (destroyed) return;
    if (article && records.has(article)) pending.add(article);
    if (!frameId && pending.size) frameId = global.requestAnimationFrame(flush);
  }

  var resizeObserver = typeof global.ResizeObserver === 'function'
    ? new global.ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          schedule(nodeOwners.get(entry.target));
        });
      })
    : null;

  // Shopify can update block settings by mutating the existing article rather
  // than reloading the whole section. Position variables and Content position
  // classes therefore need an explicit recalculation trigger.
  var mutationObserver = typeof global.MutationObserver === 'function'
    ? new global.MutationObserver(function (entries) {
        entries.forEach(function (entry) {
          if (records.has(entry.target)) schedule(entry.target);
        });
      })
    : null;

  function unobserve(record) {
    if (!resizeObserver) return;
    record.observed.forEach(function (node) {
      resizeObserver.unobserve(node);
    });
  }

  function unregister(article, clearOutput) {
    var record = records.get(article);
    if (!record) return;
    unobserve(record);
    records.delete(article);
    pending.delete(article);

    if (clearOutput) {
      if (record.title.isConnected) {
        record.title.style.removeProperty('--efa-slide-shift-x');
        record.title.style.removeProperty('--efa-slide-shift-y');
      }
      article.removeAttribute('data-efa-position-ready');
      article.removeAttribute('data-efa-position-overflow');
    }
  }

  function register(article) {
    var title = directTitle(article);
    var target = title && positionTarget(article, title);
    if (!title || !target) {
      unregister(article, true);
      return;
    }

    var frame = article.classList.contains('w-aside') ? title : article;
    var links = Array.from(target.querySelectorAll('.link-btn'));
    var ctaNodes = [];
    links.forEach(function (link) {
      ctaNodes.push(link);
      Array.from(link.children).forEach(function (child) { ctaNodes.push(child); });
    });
    var nodes = [target].concat(ctaNodes);
    var previous = records.get(article);
    var unchanged = previous && previous.title === target && previous.frame === frame && previous.nodes.length === nodes.length &&
      previous.nodes.every(function (node, index) { return node === nodes[index]; });

    if (unchanged) {
      schedule(article);
      return;
    }

    if (previous) unregister(article, false);

    var record = {
      title: target,
      frame: frame,
      nodes: nodes,
      observed: Array.from(new Set([frame].concat(nodes))),
      shiftX: cssNumber(target, '--efa-slide-shift-x'),
      shiftY: cssNumber(target, '--efa-slide-shift-y')
    };
    records.set(article, record);

    if (mutationObserver) {
      mutationObserver.observe(article, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    record.observed.forEach(function (node) {
      nodeOwners.set(node, article);
      if (resizeObserver) resizeObserver.observe(node);
    });
    schedule(article);
  }

  function candidates(root) {
    var found = [];
    if (!root) return found;
    if (root.nodeType === 1 && root.matches(SELECTOR)) found.push(root);
    if (typeof root.querySelectorAll === 'function') {
      found = found.concat(Array.from(root.querySelectorAll(SELECTOR)));
    }
    return found;
  }

  function refresh(root) {
    if (destroyed) return;
    records.forEach(function (_, article) {
      if (!article.isConnected) {
        unregister(article, false);
      } else if (!article.matches(SELECTOR)) {
        unregister(article, true);
      }
    });
    candidates(root || document).forEach(register);
  }

  function unregisterWithin(root) {
    if (!root) return;
    records.forEach(function (_, article) {
      if (root === article || (typeof root.contains === 'function' && root.contains(article))) {
        unregister(article, false);
      }
    });
  }

  function onSectionChange(event) {
    refresh(event.target || document);
  }

  function onSectionUnload(event) {
    unregisterWithin(event.target);
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (frameId) global.cancelAnimationFrame(frameId);
    frameId = 0;
    pending.clear();
    records.forEach(function (_, article) { unregister(article, true); });
    if (resizeObserver) resizeObserver.disconnect();
    if (mutationObserver) mutationObserver.disconnect();
    document.removeEventListener('shopify:section:load', onSectionChange);
    document.removeEventListener('shopify:section:reorder', onSectionChange);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    document.removeEventListener('DOMContentLoaded', start);
    if (global.EFASlideshowPosition === api) delete global.EFASlideshowPosition;
  }

  var api = {
    version: VERSION,
    refresh: refresh,
    destroy: destroy,
    calculateAxis: calculateAxis
  };
  global.EFASlideshowPosition = api;

  document.addEventListener('shopify:section:load', onSectionChange);
  document.addEventListener('shopify:section:reorder', onSectionChange);
  document.addEventListener('shopify:section:unload', onSectionUnload);

  function start() {
    if (destroyed) return;
    refresh(document);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (!destroyed) refresh(document);
      }).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})(typeof window !== 'undefined' ? window : globalThis);
